import { Component } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'

import typeStyle from '../../styles/typeStyle'
import fetchUser from '../../actions/userActions'

import Container from '../../components/layout/Container'
import Spinner from '../../components/display/Spinner'
import SimpleA from '../../components/display/SimpleAnchor'

import ProfileSections from './ProfileSections'

import InstagramIcon from '../../components/icons/Instagram'
import LocationPinIcon from '../../components/icons/LocationPin'
import ValidIcon from '../../components/icons/Valid'

import ShowMoreText from '../../components/ShowMoreText'

const SpinnerWrapper = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Section = styled.section`
  padding: 40px 0;
  margin: 0 !important;
`

const IntroSection = styled.div`
  display: flex;
`

const IntroTitle = styled.h1`
  ${typeStyle('-ds-font-title-1')}
  margin: 0;
  margin-bottom: 20px;
`

const AvatarWrapper = styled.div`
  display: none;
  padding-right: 20px;
  @media (min-width: 768px) {
    display: block;
    padding-right: 68px;
  }
`

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: black;
  margin-bottom: 20px;

  float: left;
  margin-right: 20px;

  @media (min-width: 768px) {
    float: none;
    margin-right: 0;
    width: 200px;
    height: 200px;
  }
`

// Rendered server-side and toggled with CSS (not the JS <Media> wrapper) so the
// avatar is in the initial HTML and doesn't shift content in on hydration.
const MobileAvatar = styled(Avatar)`
  @media (min-width: 768px) {
    display: none;
  }
`

const AttributesWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 20px;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const UserAttribute = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  margin-right: 20px;
  color: ${({ theme, colorful }) =>
    colorful ? theme['-ds-core-400'].value : theme.colors.labelGrey};

  svg {
    color: ${({ theme }) => theme['-ds-core-400'].value};
    height: 18px;
    margin-right: 8px;
  }
`

export class UserProfile extends Component {
  componentDidMount() {
    this.ensureUserLoaded()
    this.updateTitle()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.userId !== this.props.match.params.userId) {
      this.ensureUserLoaded()
    }

    this.updateTitle()
  }

  // The server seeds the matching user, so only fetch when it's missing or the
  // route points at a different user (client-side navigation between profiles).
  ensureUserLoaded() {
    const { dispatch, match, user } = this.props
    const { userId } = match.params
    const loaded = user.data && String(user.data.id) === String(userId)

    if (!loaded) {
      dispatch(fetchUser(userId))
    }
  }

  updateTitle() {
    const { user } = this.props

    if (user.data && typeof document !== 'undefined') {
      document.title = `${user.data.fullname} | Riparide`
    }
  }

  render() {
    const { user } = this.props

    if (user.fetching || !user.data) {
      return (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )
    }

    return (
      <Container>
        <Section>
          <IntroSection>
            <AvatarWrapper>
              <Avatar
                src={user.data.avatar_url}
                alt={`${user.data.first_name}'s profile photo`}
                data-testid="profile-avatar"
              />
            </AvatarWrapper>
            <div style={{ flex: 1 }}>
              <MobileAvatar
                src={user.data.avatar_url}
                alt={`${user.data.first_name}'s profile photo`}
                data-testid="profile-avatar"
              />
              <IntroTitle>Hi, I&apos;m {user.data.first_name}</IntroTitle>
              <AttributesWrapper>
                {user.data.location && (
                  <UserAttribute>
                    <LocationPinIcon /> {user.data.location}
                  </UserAttribute>
                )}
                {user.data.email_verified && (
                  <UserAttribute>
                    <ValidIcon /> Email Verified
                  </UserAttribute>
                )}
                {user.data.phone_number_verified && (
                  <UserAttribute>
                    <ValidIcon /> Phone Verified
                  </UserAttribute>
                )}
                {user.data.facebook_verified && (
                  <UserAttribute>
                    <ValidIcon /> Facebook Verified
                  </UserAttribute>
                )}
                {user.data?.instagram && (
                  <SimpleA
                    href={`https://instagram.com/${user.data.instagram}`}
                    target="_blank"
                    textDecoration="none"
                  >
                    <UserAttribute colorful>
                      <InstagramIcon /> @{user.data.instagram}
                    </UserAttribute>
                  </SimpleA>
                )}
              </AttributesWrapper>
              <ShowMoreText>{user.data.description}</ShowMoreText>
            </div>
          </IntroSection>
        </Section>

        <ProfileSections userId={user.data.id} />
      </Container>
    )
  }
}

export default connect(({ user }) => ({ user }))(UserProfile)
